import React, { useMemo, useState, useRef, useEffect } from "react";
import { CheckCircle2, Paperclip, Check, X } from "lucide-react";
import { useApp, formatBRL } from "../context/AppContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import PagadorField from "./PagadorField";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const hoje = () => new Date().toISOString().slice(0, 10);
const vazio = { descricao: "", categoria: "Serviço", pagador: "", valor: "", vencimento: hoje(), status: "pendente", dataRecebimento: "", recorrente: false, comprovante: "", observacoes: "" };
const CATS = ["Serviço", "Aluguel", "Reembolso", "Convênio", "Doação", "Venda", "Outros"];

const addMonthsISO = (iso, i) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const base = new Date(y, (m - 1) + i, 1);
  const ult = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(Math.min(d || 1, ult)).padStart(2, "0")}`;
};

// Modal de criação/edição de recebível manual (contas a receber avulsas).
const ReceberModal = ({ open, onOpenChange, editing }) => {
  const { salvarContaReceber, residentesAtivos, residentes } = useApp();
  const { toast } = useToast();
  const [form, setForm] = useState(vazio);
  const fileRef = useRef(null);
  const [parcelado, setParcelado] = useState(false);
  const [numParcelas, setNumParcelas] = useState(2);
  const [parcelas, setParcelas] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (editing) { setForm({ ...vazio, ...editing, valor: editing.valor || "" }); }
    else { setForm(vazio); }
    setParcelado(false); setNumParcelas(2); setParcelas([]);
  }, [open, editing]);

  // Sugestões de pagador: residentes ativos + familiares cadastrados
  const sugestoesPagador = useMemo(() => {
    const out = [];
    (residentesAtivos || []).forEach((r) => out.push({ key: `r-${r.id}`, label: r.nome, sub: "Residente" }));
    (residentes || []).forEach((r) => (r.familiares || []).forEach((f, i) => {
      if (f?.nome) out.push({ key: `f-${r.id}-${i}`, label: f.nome, sub: `Familiar de ${r.nome}` });
    }));
    const seen = new Set();
    return out.filter((s) => { const k = s.label.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
  }, [residentesAtivos, residentes]);

  const anexar = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader(); r.onload = () => setForm((f) => ({ ...f, comprovante: r.result })); r.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const gerarParcelas = (n, totalStr, primeiro) => {
    const total = parseFloat(totalStr) || 0;
    const qtd = Math.max(2, Math.min(parseInt(n) || 2, 120));
    const base = Math.floor((total / qtd) * 100) / 100;
    setParcelas(Array.from({ length: qtd }, (_, i) => ({ vencimento: addMonthsISO(primeiro || hoje(), i), valor: i === qtd - 1 ? +(total - base * (qtd - 1)).toFixed(2) : base })));
  };
  const togglarP = (v) => { setParcelado(v); if (v) gerarParcelas(numParcelas, form.valor, form.vencimento); else setParcelas([]); };
  const setNum = (v) => { setNumParcelas(v); if (parcelado) gerarParcelas(v, form.valor, form.vencimento); };
  const setParcela = (i, patch) => setParcelas((ps) => ps.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const somaP = useMemo(() => parcelas.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0), [parcelas]);

  const salvar = async () => {
    if (!form.descricao.trim()) { toast({ title: "Informe a descrição" }); return; }
    try {
      if (parcelado && !editing) {
        if (parcelas.length < 2) { toast({ title: "Gere ao menos 2 parcelas" }); return; }
        await salvarContaReceber({ descricao: form.descricao, categoria: form.categoria, pagador: form.pagador, comprovante: form.comprovante, observacoes: form.observacoes, parcelas: parcelas.map((p) => ({ vencimento: p.vencimento, valor: parseFloat(p.valor) || 0 })) });
        toast({ title: `${parcelas.length} parcelas lançadas` });
      } else {
        const payload = { ...form, valor: parseFloat(form.valor) || 0 };
        await salvarContaReceber(editing ? { ...payload, id: editing.id } : payload);
        toast({ title: editing ? "Recebível atualizado" : "Recebível lançado" });
      }
      onOpenChange(false);
    } catch (e) { toast({ title: "Erro ao salvar", description: e?.response?.data?.detail || "" }); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="receber-modal">
        <DialogHeader><DialogTitle>{editing ? "Editar recebível" : "Novo recebível"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-sm font-medium text-slate-600 mb-1 block">Descrição</label>
            <input className={inputCls} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} data-testid="rec-descricao" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Categoria</label>
              <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                <SelectTrigger data-testid="rec-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Pagador / Cliente</label>
              <PagadorField value={form.pagador} onChange={(v) => setForm({ ...form, pagador: v })} sugestoes={sugestoesPagador} testid="rec-pagador" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">{parcelado ? "Valor total (R$)" : "Valor (R$)"}</label>
              <input type="number" step="0.01" className={inputCls} value={form.valor} onChange={(e) => { setForm({ ...form, valor: e.target.value }); if (parcelado) gerarParcelas(numParcelas, e.target.value, form.vencimento); }} data-testid="rec-valor" /></div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">{parcelado ? "1º Vencimento" : "Vencimento"}</label>
              <input type="date" className={inputCls} value={form.vencimento} onChange={(e) => { setForm({ ...form, vencimento: e.target.value }); if (parcelado) gerarParcelas(numParcelas, form.valor, e.target.value); }} data-testid="rec-vencimento" /></div>
          </div>

          {!editing && (
            <div className="border border-slate-200 rounded-lg p-3 space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" checked={parcelado} onChange={(e) => togglarP(e.target.checked)} data-testid="rec-parcelado" /> Recebível parcelado
              </label>
              {parcelado && (
                <>
                  <div className="flex items-end gap-2">
                    <div><label className="text-xs text-slate-500 mb-1 block">Nº de parcelas</label>
                      <input type="number" min="2" max="120" className={`${inputCls} w-28`} value={numParcelas} onChange={(e) => setNum(e.target.value)} data-testid="rec-num-parcelas" /></div>
                    <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => gerarParcelas(numParcelas, form.valor, form.vencimento)} data-testid="rec-gerar-parcelas"><CheckCircle2 className="w-4 h-4" /> Dividir valor</Button>
                    <span className={`text-xs ml-auto ${Math.abs(somaP - (parseFloat(form.valor) || 0)) > 0.01 ? "text-amber-600" : "text-emerald-600"}`}>Soma: {formatBRL(somaP)}</span>
                  </div>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {parcelas.map((p, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 w-8">{i + 1}ª</span>
                        <input type="date" className={`${inputCls} flex-1`} value={p.vencimento} onChange={(e) => setParcela(i, { vencimento: e.target.value })} />
                        <input type="number" step="0.01" className={`${inputCls} w-32`} value={p.valor} onChange={(e) => setParcela(i, { valor: e.target.value })} />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {!parcelado && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-600 mb-1 block">Status</label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="rec-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="pendente">A receber</SelectItem><SelectItem value="recebido">Recebido</SelectItem></SelectContent>
                  </Select></div>
                {form.status === "recebido" && (
                  <div><label className="text-sm font-medium text-slate-600 mb-1 block">Data do recebimento</label>
                    <input type="date" className={inputCls} value={form.dataRecebimento || hoje()} onChange={(e) => setForm({ ...form, dataRecebimento: e.target.value })} /></div>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.recorrente} onChange={(e) => setForm({ ...form, recorrente: e.target.checked })} data-testid="rec-recorrente" /> Recebível recorrente (gera automaticamente todo mês)
              </label>
            </>
          )}

          <div><label className="text-sm font-medium text-slate-600 mb-1 block">Comprovante / nota (opcional)</label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={anexar} data-testid="rec-anexo-input" />
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" className="gap-2" onClick={() => fileRef.current?.click()}><Paperclip className="w-4 h-4" /> Anexar</Button>
              {form.comprovante && <span className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Anexado <button onClick={() => setForm({ ...form, comprovante: "" })} className="text-red-500 ml-1"><X className="w-3 h-3" /></button></span>}
            </div>
          </div>
          <div><label className="text-sm font-medium text-slate-600 mb-1 block">Observações</label>
            <textarea rows={2} className={inputCls} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={salvar} data-testid="salvar-receber">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReceberModal;
